import { createContext, useContext, FC, ReactNode, useState, useCallback, useEffect, useMemo } from 'react';

import { useVerifyDeveloperMutation } from '@carcareers/store';

interface DeveloperContextProps {
  isDeveloper: boolean;
  disableDeveloper: () => void;
}

export interface DeveloperProviderProps {
  children: ReactNode;
}

declare global {
  interface Window {
    developerDebug?: (password?: string | false) => Promise<boolean>;
  }
}

const DeveloperContext = createContext<DeveloperContextProps | undefined>(undefined);

export function useDeveloper() {
  const context = useContext(DeveloperContext);
  if (!context) {
    throw new Error('useDeveloper must be used within a DeveloperProvider');
  }
  return context;
}

export const DeveloperProvider: FC<DeveloperProviderProps> = ({ children }) => {
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [verifyDeveloper] = useVerifyDeveloperMutation();

  const disableDeveloper = useCallback(() => {
    setIsDeveloper(false);
    // eslint-disable-next-line no-console
    console.info('[developerDebug] developer mode disabled');
  }, []);

  const enableDeveloper = useCallback(
    async (password: string): Promise<boolean> => {
      try {
        const { valid } = await verifyDeveloper({ password }).unwrap();

        if (valid) {
          setIsDeveloper(true);
          // eslint-disable-next-line no-console
          console.info('[developerDebug] developer mode enabled');
          return true;
        }
      } catch {
        // fall through to the warning below
      }

      // eslint-disable-next-line no-console
      console.warn('[developerDebug] developer mode not enabled (incorrect password or not authorized)');
      return false;
    },
    [verifyDeveloper],
  );

  useEffect(() => {
    window.developerDebug = async (password?: string | false): Promise<boolean> => {
      if (!password) {
        disableDeveloper();
        return false;
      }

      return enableDeveloper(password);
    };

    return () => {
      delete window.developerDebug;
    };
  }, [enableDeveloper, disableDeveloper]);

  const value = useMemo(() => ({ isDeveloper, disableDeveloper }), [isDeveloper, disableDeveloper]);

  return <DeveloperContext.Provider value={value}>{children}</DeveloperContext.Provider>;
};
